import { orderPaymentStatuses, orderStages, projectPhases, projectTypes, projectVersions } from '@/Models';
import { orderRules } from '@/Config';
import Resources from '@/Resources.js';

// separated for brevity in following maps and buildOrderStateExplanation() function
const {
    pledged,
    placed,
    preparingForShipping,
    readyForShipping,
    partiallyShipped,
    shipped,
    completed,
    canceled,
} = orderStages;

const {
    none,
    awaitingPayment,
    paid,
    partiallyPaid,
    scheduled,
    scheduledAndAwaitingPayment,
    processingPayment,
} = orderPaymentStatuses;

const {
    crowdfunding,
    crowdfundingEnded,
    latePledge,
    pledgeManager,
    store,
    latePledgeEnded,
    pledgeManagerComplete,
} = projectPhases;

export function getOrderStageName(stage) {
    return orderStages[stage].displayName;
}

export function getOrderPaymentStatusName(paymentStatus) {
    return orderPaymentStatuses[paymentStatus].displayName;
}

export function getOrderPaymentStatusDescription(paymentStatus, phase) {
    const isStore = phase === store;
    const paymentStatusDescriptions = {
        [awaitingPayment]: isStore
            ? Resources.OrderStatus.OrderAwaitingPaymentStatusParagraph
            : Resources.OrderStatus.AwaitingPaymentStatusParagraph,
        [paid]: isStore
            ? Resources.OrderStatus.OrderPaidStatusParagraph
            : Resources.OrderStatus.PaidStatusParagraph,
        [partiallyPaid]: isStore
            ? Resources.OrderStatus.StoreNotificatorPartiallyPaidMessage
            : Resources.OrderStatus.PartiallyPaidStatusParagraph,
        [scheduled]: Resources.OrderStatus.ScheduledStatusParagraph,
        [scheduledAndAwaitingPayment]: Resources.OrderStatus.ScheduledAndAwaitingPaymentStatusParagraph,
        [processingPayment]: Resources.OrderStatus.OrderPaymentStatusProcessingPaymentDescription,
    };

    return paymentStatusDescriptions[paymentStatus];
}

export function buildOrderStateExplanation({
    stage,
    paymentStatus,
    isEditing,
    phase,
    canRetryInstallment,
    canAddNewItems,
    canProceedToCheckout,
    canResumePayment,
    canEditPledgedItems,
    hasProductsWithMissingOptions,
    publicPaymentID,
    isExpressCrowdfunding }) {
    // --- notificators ---
    // installment problem
    if (canRetryInstallment && !isEditing) {
        return {
            type: 'notificator',
            text: Resources.OrderStatus.NotificatorProblemWithCollectingInstallmentsMessage,
        };
    }
    // cta - finish pledge
    if (isEditing) {
        if (!canProceedToCheckout) { // short circuiting - editing pledge but can't checkout so no cta
            return null;
        }

        const text = stage === pledged && phase === pledgeManager
            ? Resources.OrderStatus.NotificatorShippingAddressMissingMessage
            : Resources.OrderStatus.NotificatorPledgeNotFinishedMessage;

        return {
            type: 'notificator',
            text,
            button: Resources.OrderStatus.FinishPledgeButton,
            action: 'checkout',
        };
    }

    const isOrderCanceled = stage === canceled;

    // cta - edit pledge to choose missing product options
    if (hasProductsWithMissingOptions && canEditPledgedItems && !isOrderCanceled) {
        return {
            type: 'notificator',
            theme: 'warning',
            text: Resources.OrderStatus.NotificatorMissingProductOptionsMessage,
            button: Resources.OrderStatus.EditPledgeButton,
            action: 'editPledge',
        };
    }

    // cta - pay more
    if (paymentStatus === awaitingPayment && !isEditing && !isOrderCanceled && canResumePayment) {
        return {
            type: 'notificator',
            text: phase === projectPhases.store
                ? Resources.OrderStatus.StoreResumePaymentMessage
                : Resources.OrderStatus.ResumePaymentMessage,
            button: Resources.OrderStatus.ResumePaymentButton,
            action: 'resumePayment',
        };
    }
    if (paymentStatus === scheduledAndAwaitingPayment && !isEditing && !isOrderCanceled && canResumePayment) {
        return {
            type: 'notificator',
            text: Resources.OrderStatus.ScheduledAndAwaitingPaymentStatusParagraph,
            button: Resources.OrderStatus.ResumePaymentButton,
            action: 'resumePayment',
        };
    }
    if (paymentStatus === partiallyPaid && !isEditing && !isOrderCanceled && canResumePayment) {
        return {
            type: 'notificator',
            text: phase === projectPhases.store
                ? Resources.OrderStatus.StoreNotificatorPartiallyPaidMessage
                : Resources.OrderStatus.NotificatorPartiallyPaidMessage,
            button: Resources.OrderStatus.ResumePaymentButton,
            action: 'resumePayment',
        };
    }

    // --- green ticks ---
    // all set for now - before pledge manager
    if (paymentStatus === none && stage === pledged && (phase === crowdfunding || phase === crowdfundingEnded)) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationPaymentNoneParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationPaymentNoneNextStepParagraph,
        };
    }
    if (paymentStatus === paid && (stage === pledged || stage === placed) && isExpressCrowdfunding && publicPaymentID !== null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationExpressCrowdfundingPaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationExpressCrowdfundingPaidNextStepParagraph,
        };
    }
    if (paymentStatus === scheduled && (stage === pledged || stage === placed) && isExpressCrowdfunding) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationCrowdfundingExpressScheduledHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationExpressCrowdfundingPaymentScheduledParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationExpressCrowdfundingScheduledNextStepParagraph,
        };
    }
    if (paymentStatus === paid && stage === placed && phase === crowdfunding && publicPaymentID !== null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationCrowdfundingPaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationCrowdfundingPaidNextStepParagraph,
        };
    }

    if (paymentStatus === paid && stage === pledged && phase === crowdfunding && publicPaymentID === null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationCrowdfundingNoPaymentHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationCrowdfundingNoPaymentParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationCrowdfundingNoPaymentNextStepParagraph,
        };
    }

    if (paymentStatus === paid && stage === pledged && phase === crowdfundingEnded) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationCrowdfundingEndedPaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationCrowdfundingEndedPaymentPaidNextStepParagraph,
        };
    }
    if (paymentStatus === paid && stage === pledged && phase === latePledge && publicPaymentID !== null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationLatePledgePaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationLatePledgePaymentPaidNextStepParagraph,
        };
    }
    if (paymentStatus === paid && stage === pledged && phase === latePledge && publicPaymentID === null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationLatePledgeNoPaymentHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationLatePledgeNoPaymentParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationLatePledgeNoPaymentNextStepParagraph,
        };
    }
    if (paymentStatus === paid && stage === pledged && phase === latePledgeEnded && publicPaymentID !== null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationLatePledgePaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationLatePledgeEndedNextStepParagraph,
        };
    }
    if (paymentStatus === paid && stage === pledged && phase === latePledgeEnded && publicPaymentID === null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationLatePledgeNoPaymentHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationLatePledgeNoPaymentParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationLatePledgeEndedNextStepParagraph,
        };
    }
    if (paymentStatus === scheduled && stage === pledged && phase === crowdfunding) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationCrowdfundingPaymentScheduledInstallmentsParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationCrowdfundingPaymentScheduledInstallmentsNextStepParagraph,
        };
    }
    if (paymentStatus === scheduled && stage === pledged && phase === crowdfundingEnded) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationCrowdfundingEndedPaymentScheduledInstallmentsParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationCrowdfundingEndedPaymentPaidNextStepParagraph,
        };
    }
    if (paymentStatus === scheduled && stage === pledged && phase === latePledge) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationLatePledgePaymentScheduledInstallmentsParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationLatePledgePaymentScheduledInstallmentsNextStepParagraph,
        };
    }
    if (paymentStatus === scheduled && stage === pledged && phase === latePledgeEnded) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationLatePledgePaymentScheduledInstallmentsParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationLatePledgeEndedNextStepParagraph,
        };
    }
    // pledge manager statuses
    if (paymentStatus === paid && stage === placed && phase === pledgeManager && publicPaymentID !== null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentPaidHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentPaidNextStepParagraph,
        };
    }
    if (paymentStatus === paid && stage === placed && phase === pledgeManagerComplete && publicPaymentID !== null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentPaidHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerCompleteNextStepParagraph,
        };
    }

    if (paymentStatus === paid && stage === placed && phase === pledgeManager && publicPaymentID === null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerNoPaymentHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerNoPaymentParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerNoPaymentNextStepParagraph,
        };
    }

    if (paymentStatus === paid && stage === placed && phase === pledgeManagerComplete && publicPaymentID === null) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerNoPaymentHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerNoPaymentParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerCompleteNextStepParagraph,
        };
    }

    if (paymentStatus === scheduled && stage === placed && phase === pledgeManager) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentScheduledHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentScheduledParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentScheduledNextStepParagraph,
        };
    }

    if (paymentStatus === scheduled && stage === placed && phase === pledgeManagerComplete) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentScheduledHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPaymentScheduledParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerCompleteNextStepParagraph,
        };
    }

    if ((paymentStatus === paid || paymentStatus === scheduled) && phase === pledgeManager) {
        if (stage === preparingForShipping) {
            if (canAddNewItems) {
                return {
                    type: 'tick',
                    text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingForShippingCanAddNewItemsHeader,
                    sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingForShippingCanAddNewItemsParagraph,
                    next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingForShippingCanAddNewItemsNextStepParagraph,
                };
            }

            return {
                type: 'tick',
                text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingForShippingHeader,
                sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingForShippingParagraph,
                next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingForShippingNextStepParagraph,
            };
        }
        if (stage === readyForShipping) {
            return {
                type: 'tick',
                text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingShippingHeader,
                sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingShippingParagraph,
                next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPreparingShippingNextStepParagraph,
            };
        }
        if (stage === partiallyShipped) {
            return {
                type: 'tick',
                text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPartiallyShippedHeader,
                sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPartiallyShippedParagraph,
                next: Resources.OrderStatus.OrderStatusExplanationPledgeManagerPartiallyShippedNextStepParagraph,
            };
        }
        if (stage === shipped) {
            return {
                type: 'tick',
                text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerShippedHeader,
                sub: Resources.OrderStatus.OrderStatusExplanationPledgeManagerShippedParagraph,
            };
        }
        if (stage === completed) {
            return {
                type: 'tick',
                text: Resources.OrderStatus.OrderStatusExplanationPledgeManagerCompletedHeader,
            };
        }
    }
    // store statuses
    if (paymentStatus === paid && stage === placed && phase === store) {
        return {
            type: 'tick',
            text: Resources.OrderStatus.OrderStatusExplanationAllSetHeader,
            sub: Resources.OrderStatus.OrderStatusExplanationStorePaymentPaidParagraph,
            next: Resources.OrderStatus.OrderStatusExplanationStoreNextStepParagraph,
        };
    }
    return null;
}

export function getAvailableOrderStagesForUserContext() {

    return Object.values(orderStages)
        .filter(arrayItem => orderRules.availableOrderStagesForCreatorChange.includes(arrayItem.value))
        .sort((a, b) => a.order - b.order);
}

export function getDisplayOrderStagesForProjectContext(projectContext) {
    const isV1 = projectContext.project && projectContext.project.version === projectVersions.v1;
    const stageMap = {
        // all stages are available for crowdfunding projects, so if there is no project context, we want to show all of them
        [!projectContext.project]: orderRules.orderStagesForCrowdfundingFilters,
        [isV1]: orderRules.orderStagesForStoreFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.store]: orderRules.orderStagesForStoreFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.pledgeManager]: orderRules.orderStagesForExternalPMFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.crowdfunding]: orderRules.orderStagesForCrowdfundingFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.expressCrowdfunding]: orderRules.orderStagesForExternalPMFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.preorder]: orderRules.orderStagesForPreorderFilters,
    };
    return Object.values(stageMap[true])
        .map(item => orderStages[item]);
}

export function getDisplayOrderPaymentStatuesForProjectContext(projectContext) {
    const isV1 = projectContext.project && projectContext.project.version === projectVersions.v1;
    const statusMap = {
        // all statuses are available for crowdfunding projects, so if there is no project context, we want to show all of them
        [!projectContext.project]: orderRules.orderPaymentStatusesForCrowdfundingFilters,
        [isV1]: orderRules.orderPaymentStatusesForStoreFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.store]: orderRules.orderPaymentStatusesForStoreFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.pledgeManager]: orderRules.orderPaymentStatusesForExternalPMFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.crowdfunding]: orderRules.orderPaymentStatusesForCrowdfundingFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.expressCrowdfunding]: orderRules.orderPaymentStatusesForExternalPMFilters,
        [!isV1 && projectContext.project?.originalType === projectTypes.preorder]: orderRules.orderPaymentStatusesForPreorderFilters,
    };
    return Object.values(statusMap[true])
        .map(item => orderPaymentStatuses[item]);
}

export function validateOrderStageAndPaymentStatus(cartSummary, validStates) {
    return cartSummary !== null ? validStates.some(x => x.stage === cartSummary.cart.orderStage && x.paymentStatus === cartSummary.cart.orderPaymentStatus)
    || validStates.some(x => x.stage === cartSummary.cart.parentOrder?.orderStage && x.paymentStatus === cartSummary.cart.parentOrder?.orderPaymentStatus) : false;
}

export function getOrderStageColorCode(stage) {
    return {
        [pledged]: 'success',
        [placed]: 'success',
        [preparingForShipping]: 'success',
        [readyForShipping]: 'success',
        [partiallyShipped]: 'success',
        [shipped]: 'success',
        [completed]: 'success',
        [canceled]: 'default',
    }[stage];
}

export function getOrderPaymentStatusColorCode(paymentStatus) {
    return {
        [none]: 'default',
        [awaitingPayment]: 'warning',
        [paid]: 'success',
        [partiallyPaid]: 'warning',
        [scheduled]: 'info',
        [scheduledAndAwaitingPayment]: 'warning',
        [processingPayment]: 'info',
    }[paymentStatus];
}